import { DeleteOutlined, HolderOutlined, PlusOutlined } from "@ant-design/icons"
import type { DragEndEvent } from "@dnd-kit/core"
import { DndContext } from "@dnd-kit/core"
import { restrictToVerticalAxis } from "@dnd-kit/modifiers"
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { Button, Empty, Flex, Form, Input } from "antd"
import { type FormInstance } from "antd/es/form/hooks/useForm"
import React from "react"

import { type ProjectTypeTemplateChecklistItemPrimitive } from "../../types/project-type.types.ts"
import { type ProjectTypeFormData } from "./ProjectType.tsx"

interface ProjectTypeMilestoneCheckListProps {
  form: FormInstance<ProjectTypeFormData>
}

export function ProjectTypeMilestoneCheckList({
  form
}: ProjectTypeMilestoneCheckListProps) {
  const containerRef = React.useRef<HTMLDivElement>(null)

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (!over || active.id === over.id) {
      return
    }

    const items =
      form.getFieldValue(["phase", "checklist_items_primitive"]) || []
    const activeIndex = items.findIndex(
      (_: ProjectTypeTemplateChecklistItemPrimitive, index: number) =>
        `item-${index}` === active.id
    )
    const overIndex = items.findIndex(
      (_: ProjectTypeTemplateChecklistItemPrimitive, index: number) =>
        `item-${index}` === over.id
    )

    if (activeIndex !== -1 && overIndex !== -1) {
      const newItems = arrayMove(
        items,
        activeIndex,
        overIndex
      ) as ProjectTypeTemplateChecklistItemPrimitive[]
      form.setFieldsValue({ phase: { checklist_items_primitive: newItems } })
    }
  }

  const onAddNew = () => {
    const currentItems =
      form.getFieldValue(["phase", "checklist_items_primitive"]) || []
    form.setFieldsValue({
      phase: {
        checklist_items_primitive: [
          ...currentItems,
          { name: "", description: "" }
        ]
      }
    })

    setTimeout(() => {
      if (containerRef.current) {
        containerRef.current.scrollTo({
          top: containerRef.current.scrollHeight,
          behavior: "smooth"
        })
      }
    }, 0)
  }

  return (
    <div className="space-y-3">
      <Form.List name={["phase", "checklist_items_primitive"]}>
        {(fields, { remove }) => {
          return (
            <DndContext
              modifiers={[restrictToVerticalAxis]}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={fields.map((_, i) => `item-${i}`)}
                strategy={verticalListSortingStrategy}
                disabled={fields.length < 2}
              >
                <div
                  ref={containerRef}
                  className={
                    "space-y-3 max-h-[calc(100vh-565px)] overflow-auto"
                  }
                >
                  {fields?.length ? (
                    fields.map(({ key, ...field }, index) => {
                      return (
                        <SortableItem key={key} id={`item-${index}`}>
                          <Flex flex={1} align={"center"}>
                            <Form.Item<
                              ProjectTypeFormData["phase"]["checklist_items_primitive"]
                            >
                              {...field}
                              noStyle
                              name={[field.name, "name"]}
                              rules={[
                                {
                                  required: true,
                                  message: "Please enter checklist item name",
                                  whitespace: true
                                }
                              ]}
                            >
                              <Input placeholder="Enter name" />
                            </Form.Item>

                            <Button
                              type="text"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => remove(field.name)}
                            />
                          </Flex>

                          {/* Hidden fields to save other data */}
                          <Form.Item
                            {...field}
                            name={[
                              "phase",
                              "checklist_items_primitive",
                              field.name,
                              "id"
                            ]}
                            hidden
                          >
                            <Input />
                          </Form.Item>
                        </SortableItem>
                      )
                    })
                  ) : (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={"No checklist yet"}
                    />
                  )}
                </div>
              </SortableContext>
            </DndContext>
          )
        }}
      </Form.List>

      <Form.Item>
        <Button
          size={"large"}
          type="dashed"
          onClick={onAddNew}
          block
          icon={<PlusOutlined />}
        >
          Checklist Item
        </Button>
      </Form.Item>
    </div>
  )
}

interface SortableItemProps {
  id: string
  children: React.ReactNode
}

function SortableItem({ id, children }: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isDragging
      ? {
          position: "relative",
          zIndex: 9999,
          opacity: 0.5
        }
      : {})
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={
        "border border-border px-2 py-3 rounded-card flex items-center gap-1"
      }
    >
      <Button
        type="text"
        size="small"
        icon={<HolderOutlined className={"text-lg"} />}
        style={{ cursor: "move" }}
        {...attributes}
        {...listeners}
      />

      {children}
    </div>
  )
}
